import React from 'react';
import './Privacy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-content">
        <h1>PRIVACY POLICY — SUMMIT VISIO</h1>
        <p className="effective-date">Effective Date: 27 April 2026</p>
        
        <section>
          <h2>1. INTRODUCTION</h2>
          <p>Summit Visio ("we", "us", "our") is an Agentic AI Development company based in Krugersdorp, South Africa. We are committed to protecting your personal information in compliance with the Protection of Personal Information Act, 2013 (POPIA) and the General Data Protection Regulation (GDPR) where applicable.</p>
          <p>This policy explains how we collect, use, store, and protect your personal information when you interact with us via our website (summitvisio.com), WhatsApp Business, social media platforms, or any other communication channel.</p>
        </section>

        <section>
          <h2>2. INFORMATION WE COLLECT</h2>
          <p>We may collect the following personal information:</p>
          <ul>
            <li>Full name</li>
            <li>Phone number (including WhatsApp number)</li>
            <li>Email address</li>
            <li>Business name and type</li>
            <li>WhatsApp Business Account details and WABA ID</li>
            <li>Messages and conversation history via WhatsApp and social media</li>
            <li>Payment information (processed securely via third-party providers)</li>
            <li>Any other information you voluntarily provide</li>
          </ul>
        </section>

        <section>
          <h2>3. HOW WE COLLECT INFORMATION</h2>
          <ul>
            <li>Directly from you when you message us on WhatsApp or social media</li>
            <li>Through our website contact forms at summitvisio.com</li>
            <li>Through automated AI-powered conversations on WhatsApp Business</li>
            <li>Through our WhatsApp Business onboarding flow at summitvisio.com/onboard</li>
            <li>Through payment platforms (e.g., Paystack)</li>
          </ul>
        </section>

        <section>
          <h2>4. PURPOSE OF COLLECTION</h2>
          <p>We collect and process your information to:</p>
          <ul>
            <li>Respond to your enquiries and provide customer support</li>
            <li>Provide quotes, demos, and service information</li>
            <li>Connect your WhatsApp Business Account to our Visio AI platform</li>
            <li>Process payments and manage your account</li>
            <li>Send service-related communications</li>
            <li>Improve our AI systems and service delivery</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>5. AI-POWERED CONVERSATIONS (WHATSAPP)</h2>
          <p>Our WhatsApp Business channel uses an AI-powered assistant ("Visio") built on the WhatsApp Cloud API provided by Meta Platforms, Inc. The Visio agent handles customer enquiries, provides service information, and assists with bookings and sales. Conversations are stored securely in our database to maintain context and improve service quality.</p>
          <p>When you connect your WhatsApp Business Account to our platform via Embedded Signup, we receive and store your WhatsApp Business Account ID (WABA ID), phone number ID, and access token. This data is used solely to operate the Visio AI agent on your behalf.</p>
          <p>You may request to speak with a human representative at any time by messaging "HUMAN" to our WhatsApp Business number.</p>
        </section>

        <section>
          <h2>6. LEGAL BASIS FOR PROCESSING</h2>
          <p>We process your personal information based on:</p>
          <ul>
            <li>Your consent (including consent given during WhatsApp Business onboarding)</li>
            <li>The necessity to perform a contract or take steps at your request</li>
            <li>Our legitimate business interests</li>
            <li>Compliance with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>7. SHARING OF INFORMATION AND DATA PROCESSORS</h2>
          <p>We do not sell your personal information. We use the following third-party data processors to operate our services:</p>
          <ul>
            <li><strong>Meta Platforms, Inc. (WhatsApp Cloud API)</strong> — for WhatsApp message delivery, Business Account management, and Embedded Signup. Meta's data policy: facebook.com/privacy/policy</li>
            <li><strong>Railway (railway.app)</strong> — for hosting our automation workflows and backend services</li>
            <li><strong>Neon (neon.tech)</strong> — for secure PostgreSQL database storage of client configurations and conversation history</li>
            <li><strong>Google (Gemini AI)</strong> — for AI-powered conversation processing and response generation</li>
            <li><strong>Cloudflare (cloudflare.com)</strong> — for DNS and security services</li>
            <li><strong>Groq (groq.com)</strong> — for AI-powered conversation processing and response generation</li>
            <li><strong>Vercel (vercel.com)</strong> — for hosting our web applications and client dashboard</li>
            <li><strong>Paystack</strong> — for payment processing</li>
            <li><strong>Legal authorities</strong> — when required by law</li>
          </ul>
          <p>All third-party processors are required to handle your data in accordance with applicable data protection laws including POPIA and GDPR.</p>
        </section>

        <section>
          <h2>8. DATA RETENTION</h2>
          <p>We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, or as required by law. WhatsApp conversation history is retained to provide continuity of service and may be deleted upon request. WhatsApp Business Account credentials are retained for the duration of your subscription and deleted within 30 days of account termination.</p>
        </section>

        <section>
          <h2>9. DATA SECURITY</h2>
          <p>We implement reasonable technical and organisational measures to protect your personal information against unauthorised access, loss, or destruction. These include encrypted communications (TLS/SSL), secure cloud storage, access controls, and httpOnly cookie-based authentication for our dashboard.</p>
        </section>

        <section>
          <h2>10. YOUR RIGHTS AND DATA DELETION</h2>
          <p>Under POPIA and GDPR (where applicable), you have the right to:</p>
          <ul>
            <li>Access your personal information held by us</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to the processing of your information</li>
            <li>Withdraw consent at any time</li>
            <li>Lodge a complaint with the Information Regulator</li>
          </ul>
          <p>To exercise any of these rights, contact us using the details below.</p>
          <p><strong>WhatsApp Data Deletion:</strong> To request deletion of your WhatsApp conversation data and Business Account credentials stored in our systems, submit a formal request at: <a href="https://summitvisio.com/data-deletions" target="_blank" rel="noopener noreferrer">summitvisio.com/data-deletions</a>. We will process your request within 14 business days and confirm deletion in writing.</p>
        </section>

        <section>
          <h2>11. COOKIES</h2>
          <p>Our website may use cookies to improve your browsing experience. Our dashboard (app.summitvisio.com) uses httpOnly session cookies for authentication. These cookies are not accessible to third-party scripts. You may disable non-essential cookies in your browser settings.</p>
        </section>

        <section>
          <h2>12. CHANGES TO THIS POLICY</h2>
          <p>We may update this policy from time to time. Changes will be posted on our website with an updated effective date. Continued use of our services after changes constitutes acceptance of the updated policy.</p>
        </section>

        <section>
          <h2>13. CONTACT INFORMATION</h2>
          <p><strong>Information Officer:</strong> Samuel Adeyinka</p>
          <p><strong>Business:</strong> Summit Visio</p>
          <p><strong>Location:</strong> Krugersdorp, South Africa</p>
          <p><strong>Website:</strong> summitvisio.com</p>
          <p><strong>Data Deletion Requests:</strong> summitvisio.com/data-deletions</p>
          <p><strong>Email:</strong> summitvisio@gmail.com</p>
          <br />
          <p><strong>Information Regulator (South Africa):</strong></p>
          <p><strong>Website:</strong> https://inforegulator.org.za</p>
          <p><strong>Email:</strong> enquiries@inforegulator.org.za</p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;